import {
  Action,
  AllActions,
  DeploymentUnitStatus,
  TargetStatus,
  WitboostSubcomponent,
} from '@agilelab/plugin-wb-builder-common';
import { WbSelect } from '@agilelab/plugin-wb-platform';
import { snakeCaseToTitleCase } from '@agilelab/plugin-wb-platform-common';
import {
  Box,
  makeStyles,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import LinkIcon from '@material-ui/icons/Link';
import TabIcon from '@material-ui/icons/SubdirectoryArrowRight';
import { FC, useMemo } from 'react';
import { ComponentDependencies } from '../ProvisioningCard/ComponentsTable/ComponentDependencies';
import { DeploymentPreview } from '../types';
import { DeploymentPreviewChip } from './DeploymentPreviewChip';

const useStyles = makeStyles(theme => ({
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  selectInput: {
    height: '36px',
    minHeight: 'auto',
    fontSize: theme.typography.body1.fontSize,
  },
  selectTextField: {
    minHeight: 0,
  },
}));

interface Props {
  componentPreview: DeploymentPreview;
  subComponent: Omit<DeploymentPreview, 'components'> & WitboostSubcomponent;
  advanced?: boolean;
  getAllowedActions?: (
    currentState: DeploymentUnitStatus,
    newState: TargetStatus,
  ) => Action[];
  setPreviews?: (
    updateFn: (previews: DeploymentPreview[]) => DeploymentPreview[],
  ) => void;
}

export const SubComponentRow: FC<Props> = ({
  subComponent,
  componentPreview,
  advanced = false,
  getAllowedActions,
  setPreviews,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  const preview: DeploymentPreview = useMemo(() => {
    // State is currently tied to the parent component preview, while action is free to be set per subcomponent
    return {
      ...subComponent,
      status: subComponent.status as DeploymentUnitStatus,
      statusVersion: subComponent.statusVersion,
      newState: componentPreview.newState,
      action: subComponent.action,
      autoUpdated: subComponent.autoUpdated,
    } as DeploymentPreview;
  }, [subComponent, componentPreview.newState]);

  // Updates the subcomponent action in the appropriate parent preview entry
  const handleActionChange = (newAction: Action) => {
    setPreviews?.(previews => {
      const updatedPreviews = [...previews];
      const parentIndex = updatedPreviews.findIndex(
        p => p.id === componentPreview.id,
      );

      if (parentIndex !== -1) {
        const subComponentIndex = updatedPreviews[
          parentIndex
        ].components?.findIndex(c => c.id === subComponent.id);

        if (subComponentIndex !== undefined && subComponentIndex >= 0) {
          const updatedSubComponent =
            updatedPreviews[parentIndex].components![subComponentIndex];
          updatedPreviews[parentIndex].components![subComponentIndex] = {
            ...updatedSubComponent,
            action: newAction,
          };
        }
      }
      return updatedPreviews;
    });
  };

  return (
    <TableRow
      style={{
        background: preview.autoUpdated ? theme.palette.grey[100] : 'inherit',
      }}
    >
      <TableCell>
        <Box className={classes.nameCell}>
          <TabIcon fontSize="small" htmlColor={theme.palette.grey[500]} />
          {preview.autoUpdated && <LinkIcon color="primary" />}
          <Typography variant="body2" style={{ fontWeight: 700 }}>
            {preview.name}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {snakeCaseToTitleCase(preview.kind)}
        </Typography>
      </TableCell>
      <TableCell>
        <ComponentDependencies preview={preview} />
      </TableCell>
      <TableCell>
        <Box
          style={{
            display: 'flex',
            gap: '4px',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: theme.palette.accent.main,
          }}
        >
          <DeploymentPreviewChip
            status={preview.status}
            version={preview.statusVersion}
          />
          <ArrowForwardIcon htmlColor={theme.palette.accent.main} />
        </Box>
      </TableCell>
      <TableCell style={{ maxWidth: 220, minWidth: 220, width: 220 }}>
        <DeploymentPreviewChip
          status={preview.newState! as unknown as DeploymentUnitStatus}
        />
      </TableCell>
      <TableCell
        style={advanced ? { maxWidth: 300, minWidth: 300, width: 300 } : {}}
      >
        {advanced ? (
          <WbSelect
            ariaLabel="action"
            value={preview.action}
            disabled={
              (getAllowedActions?.(preview.status, preview.newState!) || [])
                .length <= 1
            }
            InputProps={{
              className: classes.selectInput,
            }}
            className={classes.selectTextField}
            options={
              getAllowedActions?.(preview.status, preview.newState!) ||
              AllActions
            }
            onChange={e => handleActionChange(e.target.value as Action)}
            getOptionLabel={value => snakeCaseToTitleCase(value)}
          />
        ) : (
          <Typography variant="body2" style={{ fontWeight: 700 }}>
            {preview.action && snakeCaseToTitleCase(preview.action)}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};
